import { useEffect, useState } from 'react'
import type { Cluster } from './types'
import { clipReadyAtMs, isClipReady } from './activity'

/**
 * Whether this incident's clip is finished, re-rendering when it becomes so.
 *
 * One hook for both the clip panel and the acknowledge gate. They have now been
 * wrong in three different ways by each deciding "is this finished" for itself —
 * a green dot beside a playable clip, and an Acknowledge button live while the
 * panel read "Preparing clip…". Sharing the question is what stops a fourth.
 *
 * Nothing re-renders when a window merely elapses, so the deadline is armed as a
 * timer. The clock is otherwise read only at mount and inside the effect, never
 * during render.
 */
export function useClipReady(cluster: Cluster | null): boolean {
  // No incident, no clip: a camera's live view holds this hook before one opens.
  const readyAtMs = cluster ? clipReadyAtMs(cluster) : NaN
  // Seeded from the clock once, at mount, rather than read on every render: an
  // incident settled long ago is ready immediately, instead of spending a tick
  // disabled while a zero-delay timer catches up. A lazy initialiser runs once,
  // so this stays out of the render path.
  const [armedFor, setArmedFor] = useState<number | null>(
    () => (Date.now() >= readyAtMs ? readyAtMs : null),
  )
  const armed = armedFor === readyAtMs

  useEffect(() => {
    // No moment to wait for — an older server with a detection still running.
    // The update that ends the detection brings one.
    if (armed || !Number.isFinite(readyAtMs)) return
    const t = setTimeout(() => setArmedFor(readyAtMs), Math.max(0, readyAtMs - Date.now()))
    return () => clearTimeout(t)
  }, [armed, readyAtMs])

  // The timer says the window has closed; isClipReady says whether the incident
  // also stayed ended. It can go back to false — a detection rejoining the
  // cluster reopens it, which was seen happening five times in a minute.
  return armed && !!cluster && isClipReady(cluster)
}
